import type { CSSProperties, ReactNode } from 'react';
import { motion } from 'motion/react';
import { CloseCircle } from 'iconsax-reactjs';
import { cn } from '@/lib/utils';

interface AlertBarProps {
  icon: ReactNode;
  /** Kept for callers that tint the icon tile themselves; the accent is used otherwise. */
  iconClassName?: string;
  iconStyle?: CSSProperties;
  /** The alert's colour: any CSS colour, including var(--bull). Tints the whole bar. */
  accent: string;
  /** What kind of alert this is, shown small above the title: "Price alert". */
  kind?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  /** The figure the alert is about: a price, a countdown. */
  value?: ReactNode;
  /** Small caption over the value: "Now", "Fires in". */
  valueCaption?: ReactNode;
  /** Controls, e.g. snooze buttons; grouped in one pill. Wrap to a new line when narrow. */
  actions?: ReactNode;
  onClose: () => void;
  closeLabel: string;
  closeTitle?: string;
  /** Dashed border: something not yet confirmed. */
  dashed?: boolean;
  /** Ringing right now: the icon ripples. */
  live?: boolean;
  /** A bar along the bottom edge, 0..1. */
  progress?: { value: number; color: string };
  role?: 'alert' | 'status';
}

/** `color` at `pct`% strength, for tints of any CSS colour (hex or var()). */
const tint = (color: string, pct: number) => `color-mix(in oklch, ${color} ${pct}%, transparent)`;

/**
 * One alert, as a full-width bar under the top bar.
 *
 * Every alert in the app — price lines, knots, time points — renders through
 * this, so they share one look and stack as a tidy column at the top of the
 * page. The bar is washed in its own colour from the leading edge, so a glance
 * tells price alerts from knots from time points before any text is read; a
 * ringing alarm's icon ripples, a silent one sits still.
 *
 * The root is a motion element so bars animate in and out inside an
 * AnimatePresence; the caller gives it the `key`.
 */
export function AlertBar({
  icon,
  iconClassName,
  iconStyle,
  accent,
  kind,
  title,
  subtitle,
  value,
  valueCaption,
  actions,
  onClose,
  closeLabel,
  closeTitle,
  dashed = false,
  live = false,
  progress,
  role = 'status',
}: AlertBarProps) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -10, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 420, damping: 34 }}
      role={role}
      aria-live={role === 'alert' ? 'assertive' : 'polite'}
      className="pointer-events-auto"
    >
      <div
        className={cn(
          'relative overflow-hidden rounded-xl border bg-elevated/70 backdrop-blur-xl backdrop-saturate-150',
          dashed && 'border-dashed',
        )}
        style={{
          borderColor: dashed ? tint(accent, 70) : tint(accent, 30),
          // Glass edge highlight, plus a soft glow in the alert's own colour.
          boxShadow: `inset 0 1px 0 0 ${tint('white', 8)}, 0 10px 30px -14px ${tint(accent, 55)}`,
        }}
      >
        {/* colour wash from the leading edge */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{ background: `linear-gradient(to right, ${tint(accent, 16)}, transparent 55%)` }}
        />

        <div className="relative flex flex-wrap items-center gap-x-4 gap-y-2 py-2.5 ps-4 pe-11 sm:pe-2.5">
          {/* icon */}
          <span className="relative flex size-9 shrink-0 items-center justify-center">
            {live && (
              <span
                aria-hidden="true"
                className="absolute inset-0 animate-ping rounded-xl"
                style={{ backgroundColor: tint(accent, 35) }}
              />
            )}
            <span
              className={cn('relative flex size-9 items-center justify-center rounded-xl', iconClassName)}
              style={{
                backgroundColor: tint(accent, 18),
                color: accent,
                boxShadow: `inset 0 0 0 1px ${tint(accent, 35)}`,
                ...iconStyle,
              }}
            >
              {icon}
            </span>
          </span>

          {/* what happened */}
          <div className="min-w-0 flex-1 basis-44">
            {kind && (
              <p
                className="mb-1 text-[10px] leading-none font-semibold tracking-[0.08em] uppercase"
                style={{ color: accent }}
              >
                {kind}
              </p>
            )}
            <p className="truncate text-sm leading-tight font-semibold">{title}</p>
            {subtitle && <div className="mt-0.5 truncate text-xs text-muted-foreground">{subtitle}</div>}
          </div>

          {/* the figure */}
          {value && (
            <div className="shrink-0 sm:text-end">
              {valueCaption && (
                <p className="text-[10px] leading-none font-medium tracking-wide text-muted-foreground uppercase">
                  {valueCaption}
                </p>
              )}
              <div className="tabular mt-1 flex items-baseline gap-1.5 text-xl leading-none font-semibold tracking-tight sm:justify-end">
                {value}
              </div>
            </div>
          )}

          {/* controls, grouped */}
          {actions && (
            <div className="flex shrink-0 flex-wrap items-center gap-0.5 rounded-lg bg-muted/60 p-0.5 text-[11px]">
              {actions}
            </div>
          )}

          <button
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            title={closeTitle ?? closeLabel}
            // Pinned to the corner on narrow screens, where the row wraps and an
            // inline close button would land somewhere arbitrary.
            className="absolute end-2 top-2 flex size-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground sm:static"
          >
            <CloseCircle size={18} />
          </button>
        </div>

        {progress && (
          <div className="relative h-1 w-full" style={{ backgroundColor: tint(progress.color, 15) }}>
            <div
              className="h-full transition-[width] duration-1000 ease-linear"
              style={{
                width: `${Math.max(0, Math.min(1, progress.value)) * 100}%`,
                backgroundColor: progress.color,
              }}
            />
          </div>
        )}
      </div>
    </motion.div>
  );
}

/** A button inside the bar's controls pill. */
export function AlertBarButton({
  children,
  onClick,
  label,
}: {
  children: ReactNode;
  onClick: () => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="tabular rounded-md px-2 py-1 font-medium text-muted-foreground transition-colors hover:bg-background hover:text-foreground hover:shadow-sm"
    >
      {children}
    </button>
  );
}
